import { useCallback, useRef, useState, type ReactNode } from 'react'

import ImageCropDialog, { type CropAspectOption } from './image-crop-dialog'
import { isCroppableImage, type CropOutput } from './crop-image'

export interface UseImageCropOptions {
    title?: string
    /** Proporção fixa (ex.: 1 para quadrada). */
    aspect?: number
    /** Opções de proporção para escolher no diálogo (a primeira é a inicial). */
    aspects?: CropAspectOption[]
    /** Prévia redonda, para foto de perfil. */
    round?: boolean
    output?: CropOutput
}

interface PendingCrop {
    file: File
    resolve: (file: File | null) => void
}

/**
 * Põe o diálogo de corte entre o escolher e o enviar de qualquer campo de imagem.
 *
 * Uso: `const { cropFile, cropFiles, cropDialog } = useImageCrop({ aspect: 1 })`;
 * renderize `cropDialog` e, no onChange do input, `const cut = await cropFile(file)`
 * (nulo = o usuário cancelou; não envie). `cropFiles` corta vários em sequência —
 * o que não é recortável (vídeo, GIF, SVG) passa direto, e cancelar pula só aquele.
 */
export function useImageCrop(options: UseImageCropOptions = {}) {
    const [current, setCurrent] = useState<PendingCrop | null>(null)
    const queueRef = useRef<PendingCrop[]>([])

    const advance = useCallback(() => {
        const next = queueRef.current.shift() ?? null
        setCurrent(next)
    }, [])

    const cropFile = useCallback(
        (file: File): Promise<File | null> => {
            if (!isCroppableImage(file)) return Promise.resolve(file)
            return new Promise((resolve) => {
                const pending = { file, resolve }
                setCurrent((active) => {
                    if (active) {
                        queueRef.current.push(pending)
                        return active
                    }
                    return pending
                })
            })
        },
        [],
    )

    const cropFiles = useCallback(
        async (files: File[]): Promise<File[]> => {
            const result: File[] = []
            for (const file of files) {
                const cropped = await cropFile(file)
                if (cropped) result.push(cropped)
            }
            return result
        },
        [cropFile],
    )

    const finish = useCallback(
        (file: File | null) => {
            current?.resolve(file)
            advance()
        },
        [current, advance],
    )

    const cropDialog: ReactNode = (
        <ImageCropDialog
            file={current?.file ?? null}
            title={options.title}
            aspect={options.aspect}
            aspects={options.aspects}
            round={options.round}
            output={options.output}
            onDone={(file) => finish(file)}
            onCancel={() => finish(null)}
        />
    )

    return { cropFile, cropFiles, cropDialog }
}
